import type {FabricObject, Point} from '@postermywall/fabricjs-2';
import type {ResizeCornerHandle, ResizeDrift, ResizeHandle, ResizeSideHandle, ResizeSnapInput, ResizeSnapResult, SnapAxis, SnapGuide, SnapLine, SnapPoint} from './alignment-guides.types';
import {NO_RESIZE_DRIFT, RESIZE_SIDE_HANDLES, SnapLineStyle} from './alignment-guides.types';

/**
 * Resize snapping for the editor's own resize handles. The handles grow the object by the pointer's
 * movement since the previous event, so a snap applied to the object would be carried into every later step
 * and the object could never leave the snap while the pointer moves slowly. The caller therefore keeps the
 * {@link ResizeDrift} this returns and hands it back next time, and {@link applyResizeSnap} first removes it
 * to recover the un-snapped geometry before deciding again.
 *
 * A corner handle may snap on both scene axes; a side handle only across its own edge. Only objects at a
 * multiple of 90° are snapped: a rotated object's handle cannot be moved along one scene axis by scaling alone.
 */

interface AxisMatch {
  axis: SnapAxis;
  distance: number;
  value: number;
  point: SnapPoint | null;
  guide: SnapGuide | null;
}

/** `getCoords()` order is tl, tr, br, bl in the object's own frame, so flips and rotation are already applied. */
const CORNER_INDEX: Record<ResizeCornerHandle, number> = {tl: 0, tr: 1, br: 2, bl: 3};
/** The two `getCoords()` corners a side handle sits midway between. */
const SIDE_CORNER_INDICES: Record<ResizeSideHandle, [number, number]> = {ml: [0, 3], mr: [1, 2], mt: [0, 1], mb: [3, 2]};
/** The handle that stays put while its counterpart is dragged. */
const OPPOSITE_HANDLE: Record<ResizeHandle, ResizeHandle> = {tl: 'br', tr: 'bl', br: 'tl', bl: 'tr', ml: 'mr', mr: 'ml', mt: 'mb', mb: 'mt'};

export const applyResizeSnap = (input: ResizeSnapInput): ResizeSnapResult => {
  const {target, handle, isUniform, candidates, guides, margin, drift} = input;
  removeDrift(target, handle, drift);
  if (target.getTotalAngle() % 90 !== 0) {
    return {lines: [], drift: NO_RESIZE_DRIFT};
  }

  const handlePoint = getHandlePoint(target, handle);
  const matches = getSnappableAxes(target, handle)
    .map((axis) => findNearestOnAxis(handlePoint, candidates, guides, axis, margin))
    .filter((match): match is AxisMatch => match !== null);
  // A proportional resize can only honour one axis; take the closer match.
  const applied = isUniform ? matches.sort((a, b) => a.distance - b.distance).slice(0, 1) : matches;

  const scaleBefore = {x: target.scaleX, y: target.scaleY};
  const lines: SnapLine[] = [];
  for (const match of applied) {
    if (scaleHandleTo(target, handle, match.axis, match.value, isUniform)) {
      lines.push(toSnapLine(match, getHandlePoint(target, handle)));
    }
  }
  return {lines, drift: {scaleX: target.scaleX - scaleBefore.x, scaleY: target.scaleY - scaleBefore.y}};
};

/** Puts the object back where the pointer alone would have it, undoing the scale the last snap added. */
export const removeDrift = (target: FabricObject, handle: ResizeHandle, drift: ResizeDrift): void => {
  if (drift.scaleX === 0 && drift.scaleY === 0) {
    return;
  }
  rescaleAroundAnchor(target, handle, target.scaleX - drift.scaleX, target.scaleY - drift.scaleY);
};

const isSideHandle = (handle: ResizeHandle): handle is ResizeSideHandle => RESIZE_SIDE_HANDLES.some((sideHandle) => sideHandle === handle);

/** A side handle moves only across its edge: along the object's width for ml/mr, its height for mt/mb, mapped to scene axes by the rotation. */
const getSnappableAxes = (target: FabricObject, handle: ResizeHandle): SnapAxis[] => {
  if (!isSideHandle(handle)) {
    return ['x', 'y'];
  }
  const widthRunsAlongSceneX = target.getTotalAngle() % 180 === 0;
  const movesAlongWidth = handle === 'ml' || handle === 'mr';
  return [movesAlongWidth === widthRunsAlongSceneX ? 'x' : 'y'];
};

/** Grow or shrink the object along `axis` so the dragged handle reaches `to`, keeping the opposite handle where it is. */
const scaleHandleTo = (target: FabricObject, handle: ResizeHandle, axis: SnapAxis, to: number, isUniform: boolean): boolean => {
  const handlePoint = getHandlePoint(target, handle);
  const anchor = getAnchorPoint(target, handle);
  const extent = Math.abs(handlePoint[axis] - anchor[axis]);
  if (extent === 0) {
    return false;
  }
  const outward = Math.sign(handlePoint[axis] - anchor[axis]);
  const factor = (extent + (to - handlePoint[axis]) * outward) / extent;
  if (factor <= 0) {
    return false;
  }
  // At 90° or 270° the object's width runs along scene y, so the other scale governs this axis.
  const axisUsesScaleX = (target.getTotalAngle() % 180 === 0) === (axis === 'x');
  const scaleX = isUniform || axisUsesScaleX ? target.scaleX * factor : target.scaleX;
  const scaleY = isUniform || !axisUsesScaleX ? target.scaleY * factor : target.scaleY;
  rescaleAroundAnchor(target, handle, scaleX, scaleY);
  return true;
};

const rescaleAroundAnchor = (target: FabricObject, handle: ResizeHandle, scaleX: number, scaleY: number): void => {
  const anchorBefore = getAnchorPoint(target, handle);
  target.set({scaleX, scaleY});
  target.setCoords();
  const anchorAfter = getAnchorPoint(target, handle);
  target.set({left: target.left + anchorBefore.x - anchorAfter.x, top: target.top + anchorBefore.y - anchorAfter.y});
  target.setCoords();
};

const getHandlePoint = (target: FabricObject, handle: ResizeHandle): Point => {
  const coords = target.getCoords();
  if (isSideHandle(handle)) {
    const [first, second] = SIDE_CORNER_INDICES[handle];
    return coords[first].midPointFrom(coords[second]);
  }
  return coords[CORNER_INDEX[handle]];
};

const getAnchorPoint = (target: FabricObject, handle: ResizeHandle): Point => getHandlePoint(target, OPPOSITE_HANDLE[handle]);

const findNearestOnAxis = (handlePoint: Point, candidates: SnapPoint[], guides: SnapGuide[], axis: SnapAxis, margin: number): AxisMatch | null => {
  let best: AxisMatch | null = null;
  const consider = (value: number, point: SnapPoint | null, guide: SnapGuide | null): void => {
    const distance = Math.abs(value - handlePoint[axis]);
    if (distance <= margin && (!best || distance < best.distance)) {
      best = {axis, distance, value, point, guide};
    }
  };
  for (const candidate of candidates) {
    consider(candidate[axis], candidate, null);
  }
  for (const guide of guides) {
    if (guide.axis === axis) {
      consider(guide.value, null, guide);
    }
  }
  return best;
};

const toSnapLine = (match: AxisMatch, snappedPoint: Point): SnapLine => {
  if (match.guide) {
    return {axis: match.axis, value: match.guide.value, from: match.guide.from, to: match.guide.to, style: SnapLineStyle.GUIDE};
  }
  const other: SnapAxis = match.axis === 'x' ? 'y' : 'x';
  const matchedPoint = match.point ?? snappedPoint;
  return {
    axis: match.axis,
    value: match.value,
    from: Math.min(snappedPoint[other], matchedPoint[other]),
    to: Math.max(snappedPoint[other], matchedPoint[other]),
    style: SnapLineStyle.OBJECT,
  };
};
